import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { type AxiosError } from "axios";
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import CoAppDatagridHeader from "@/components/global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "@/components/global/components/datagrid/CoAppStandardDataGrid";
import { selectionRowContainerSx } from "@/components/global/components/datagrid/dataGridSelectionRowStyles";
import useCoAppDataGridFilters from "@/components/global/components/datagrid/useCoAppDataGridFilters";
import CoAppIconTextButton from "@/components/global/components/inputs/CoAppIconTextButton";
import CoAppDestructiveConfirmationModal from "@/components/global/components/modals/CoAppDestructiveConfirmationModal";
import { CoAppActionButton } from "@/components/global/styled/global.styled";
import { VARIABLE_DATA_TYPE_OPTIONS } from "@/constants/rule-variables";
import messageLevels from "@/constants/message-levels";
import messages from "@/constants/messages";
import pages from "@/constants/pages";
import useAuthHeader from "@/hooks/useAuthHeader";
import useDatagridSorting from "@/hooks/useDatagridSorting";
import useToastAlert from "@/hooks/useToastAlert";

import { useCollectionEntryColumns } from "./columns/collection-entry-columns";
import type { Collection } from "./collectionsApi";
import {
	createCollectionEntriesApi,
	fetchCollectionDetail,
	updateCollectionEntriesApi,
} from "./collectionsApi";
import CollectionDetailSummary from "./CollectionDetailSummary";
import CollectionImportCsvButton from "./CollectionImportCsvButton";
import { useCollections } from "./CollectionsContext";
import EditCollectionNameModal from "./modals/EditCollectionNameModal";
import { buildEntriesFromCsv } from "./collectionEntryCsvImport";
import {
	buildEntrySaveDiff,
	collectionEntryFromDto,
	createDraftCollectionEntry,
	entryIdsEqual,
	entryValueIsEmpty,
	findDuplicateEntryIndices,
	normalizeCollectionEntry,
	type CollectionEntry,
} from "./collectionEntryUtils";

function cloneEntries(entries: CollectionEntry[]): CollectionEntry[] {
	return entries.map((e) => ({ ...normalizeCollectionEntry(e) }));
}

function entriesEqual(a: CollectionEntry[], b: CollectionEntry[]): boolean {
	if (a.length !== b.length) return false;
	return a.every((entry, i) => entryIdsEqual(entry.id, b[i].id) && entry.value === b[i].value);
}

const entriesGridShellSx = {
	width: "75%",
	minWidth: 360,
	maxWidth: 1400,
	display: "flex",
	flexDirection: "column",
	flex: 1,
	minHeight: 0,
} as const;

export default function CollectionDetail() {
	const { collectionId } = useParams<{ collectionId: string }>();
	const navigate = useNavigate();
	const authHeader = useAuthHeader();
	const { deleteCollections, renameCollection, isCollectionNameTaken, getCollectionById } = useCollections();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("collectionEntries");

	const [collection, setCollection] = useState<Collection | null>(null);
	const [savedEntries, setSavedEntries] = useState<CollectionEntry[]>([]);
	const [draftEntries, setDraftEntries] = useState<CollectionEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [notFound, setNotFound] = useState(false);
	const csvInputRef = useRef<HTMLInputElement>(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const dataTypeOption = collection
		? VARIABLE_DATA_TYPE_OPTIONS.find((o) => o.id === collection.dataTypeId)
		: undefined;
	const dataType = dataTypeOption?.value ?? "text";

	const loadDetail = useCallback(async () => {
		if (!collectionId) return;
		setLoading(true);
		setNotFound(false);
		try {
			const data = await fetchCollectionDetail(collectionId, authHeader);
			const { entries, ...meta } = data;
			setCollection(meta);
			const mapped = cloneEntries(entries.map(collectionEntryFromDto));
			setSavedEntries(mapped);
			setDraftEntries(cloneEntries(mapped));
		} catch (err) {
			const status = (err as AxiosError)?.response?.status;
			if (status === 404) {
				setNotFound(true);
			} else {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, "Could not load collection.");
				const fallback = collectionId ? getCollectionById(collectionId) : undefined;
				if (fallback) {
					setCollection(fallback);
				}
			}
		} finally {
			setLoading(false);
		}
	}, [authHeader, collectionId, getCollectionById, handleToastAlert]);

	useEffect(() => {
		void loadDetail();
	}, [loadDetail]);

	useEffect(() => {
		handleInitializeSortModel();
	}, [handleInitializeSortModel]);

	const duplicateIndices = useMemo(
		() => findDuplicateEntryIndices(draftEntries, dataType),
		[draftEntries, dataType],
	);

	const hasEmptyValues = draftEntries.some((e) => entryValueIsEmpty(e.value));
	const hasValidationErrors = hasEmptyValues || duplicateIndices.size > 0;
	const isDirty = !entriesEqual(draftEntries, savedEntries);

	const collectionInUse = (collection?.rulesUsingCount ?? 0) > 0;

	const updateEntryValue = useCallback((entryId: CollectionEntry["id"], value: string) => {
		setDraftEntries((prev) =>
			prev.map((e) => (entryIdsEqual(e.id, entryId) ? { ...e, value } : e)),
		);
	}, []);

	const insertRowBelow = useCallback((afterIndex: number) => {
		const newEntry = createDraftCollectionEntry("");
		setDraftEntries((prev) => {
			const next = [...prev];
			next.splice(afterIndex + 1, 0, newEntry);
			return next;
		});
	}, []);

	const deleteRow = useCallback((entryId: CollectionEntry["id"]) => {
		setDraftEntries((prev) => prev.filter((e) => !entryIdsEqual(e.id, entryId)));
	}, []);

	const columns = useCollectionEntryColumns({
		dataType,
		draftEntries,
		duplicateIndices,
		insertRowBelow,
		deleteRow,
		updateEntryValue,
	});

	const {
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		apiRef,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(draftEntries, loading);

	const handleSave = async () => {
		if (!collectionId || !isDirty || hasValidationErrors || saving) return;
		setSaving(true);
		try {
			if (savedEntries.length === 0 && draftEntries.length > 0) {
				const response = await createCollectionEntriesApi(
					collectionId,
					{ entries: draftEntries.map((e) => ({ value: e.value })) },
					authHeader,
				);
				const next = cloneEntries(response.entries.map(collectionEntryFromDto));
				setSavedEntries(next);
				setDraftEntries(cloneEntries(next));
			} else {
				const diff = buildEntrySaveDiff(savedEntries, draftEntries);
				await updateCollectionEntriesApi(collectionId, diff, authHeader);
				await loadDetail();
			}
			handleToastAlert(messageLevels.SUCCESS, "Collection entries saved.");
		} catch (err) {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, "Could not save collection entries.");
		} finally {
			setSaving(false);
		}
	};

	const handleImportClick = () => {
		csvInputRef.current?.click();
	};

	const handleCsvFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;

		try {
			const text = await file.text();
			const result = buildEntriesFromCsv(text, dataType);
			if (!result.ok) {
				const preview = result.errors
					.slice(0, 3)
					.map((e) => (e.line > 0 ? `Line ${e.line}: ${e.message}` : e.message))
					.join("; ");
				const more = result.errors.length > 3 ? ` (+${result.errors.length - 3} more)` : "";
				handleToastAlert(messageLevels.ERROR, `Import rejected. ${preview}${more}`);
				return;
			}

			setDraftEntries((prev) => [...prev, ...result.entries]);
			handleToastAlert(
				messageLevels.SUCCESS,
				`Imported ${result.entries.length} ${result.entries.length === 1 ? "entry" : "entries"}. Save to keep changes.`,
			);
		} catch {
			handleToastAlert(messageLevels.ERROR, "Could not read CSV file.");
		}
	};

	const handleRename = async (id: string, name: string) => {
		await renameCollection(id, name);
		setCollection((prev) => (prev ? { ...prev, name } : prev));
	};

	if (!collectionId || notFound) {
		return <Navigate to={pages.collectionManagement} replace />;
	}

	if (!collection && !loading) {
		return <Navigate to={pages.collectionManagement} replace />;
	}

	if (!collection) {
		return null;
	}

	const confirmDeleteCollection = async () => {
		if (!collectionId || collectionInUse) return;
		try {
			await deleteCollections([collectionId]);
			setDeleteDialogOpen(false);
			navigate(pages.collectionManagement);
		} catch {
			// toast in provider
		}
	};

	const summaryActions = (
		<Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
			<CoAppIconTextButton
				icon={<Edit sx={{ fontSize: "16px", marginRight: "5px" }} />}
				text="Edit Collection name"
				onClick={() => setEditDialogOpen(true)}
			/>
			<Tooltip title={collectionInUse ? messages.COLLECTION_IN_USE_DELETE_TOOLTIP : ""} arrow>
				<span>
					<CoAppIconTextButton
						icon={<Delete sx={{ fontSize: "16px", marginRight: "5px" }} />}
						text="Delete Collection"
						onClick={() => setDeleteDialogOpen(true)}
						isDestructive={true}
						disabled={collectionInUse}
					/>
				</span>
			</Tooltip>
		</Stack>
	);

	const importCsvControl = (
		<>
			<input
				ref={csvInputRef}
				type="file"
				accept=".csv,text/csv"
				hidden
				onChange={handleCsvFileChange}
			/>
			<CollectionImportCsvButton onClick={handleImportClick} />
		</>
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				overflow: "hidden",
			}}
		>
			<CollectionDetailSummary collection={collection} trailing={summaryActions} />

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					justifyContent: "center",
					overflow: "hidden",
					px: 2,
				}}
			>
				<Box sx={entriesGridShellSx}>
					<CoAppDatagridHeader
						iconKind="collections"
						searchIsPresent={true}
						{...quickFilterSearchProps}
						searchPlaceholder="Search entries…"
						filterControl={filterControl}
						trailingControl={importCsvControl}
						title="Entries"
						addIsPresent={false}
						editIsPresent={false}
						deleteIsPresent={false}
					/>
					<CoAppStandardDataGrid
						apiRef={apiRef}
						filterButtonEl={filterButtonEl}
						filterModel={gridFilterModel}
						onFilterModelChange={onFilterModelChange}
						visibleRowCount={visibleRowCount}
						rows={draftEntries}
						columns={columns}
						allowSelection={false}
						targetResource="collection-entries"
						gridKey={`collection-entries-${collectionId}`}
						loading={loading}
						sortModel={sortModel}
						handleSortModelChange={handleSortModelChange}
						selectionFooter={
							<Box sx={selectionRowContainerSx}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
									{draftEntries.length === 0 ? (
										<>
											<Tooltip title="Add first entry">
												<IconButton
													onClick={() => insertRowBelow(-1)}
													sx={{ color: "#02D5AB" }}
													size="small"
												>
													<Add />
												</IconButton>
											</Tooltip>
											<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.55)" }}>
												No entries yet. Add a row to get started.
											</Typography>
										</>
									) : hasValidationErrors && isDirty ? (
										<Typography variant="body2" sx={{ color: "#DE3730" }}>
											Fix empty or duplicate values before saving.
										</Typography>
									) : (
										<Typography
											variant="body2"
											sx={{ color: "rgba(13, 31, 48, 0.45)", fontWeight: 600, fontSize: 13 }}
										>
											{visibleRowCount ?? draftEntries.length}{" "}
											{(visibleRowCount ?? draftEntries.length) === 1 ? "entry" : "entries"}
											{visibleRowCount !== undefined && visibleRowCount !== draftEntries.length
												? ` (${draftEntries.length} total)`
												: ""}
										</Typography>
									)}
								</Box>
								<CoAppActionButton
									onClick={() => void handleSave()}
									disabled={!isDirty || hasValidationErrors || saving}
								>
									Save
								</CoAppActionButton>
							</Box>
						}
					/>
				</Box>
			</Box>
			<EditCollectionNameModal
				dialogOpen={editDialogOpen}
				initialName={collection.name}
				collectionId={String(collection.id)}
				onClose={() => setEditDialogOpen(false)}
				onSave={handleRename}
				isNameTaken={isCollectionNameTaken}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteDialogOpen}
				dialogTitle={`Delete ${collection.name} collection?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(collection.name, "collection")}
				confirmClickHandler={() => void confirmDeleteCollection()}
				cancelClickHandler={() => setDeleteDialogOpen(false)}
				actionText="Delete"
			/>
		</Box>
	);
}
